import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  X, RefreshCw, Users, FileText, Megaphone, 
  Rocket, Heart, AlertCircle, CheckCircle2, Loader2, Calendar, Circle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Change management lifecycle - focused on the PEOPLE side
const STAGE_CONFIG = {
  assess: {
    icon: Users,
    title: 'Assess',
    description: 'Understand who\'s affected and how',
    timeframe: 'Start here',
    fields: [
      { key: 'who_affected', label: "Who's Affected" },
      { key: 'impact_level', label: 'How Big is the Change' },
      { key: 'resistance', label: 'Likely Resistance' },
      { key: 'readiness', label: 'Change Readiness' },
    ],
    recommended: [
      { activity: 'List everyone affected by this change', tip: 'Teams, roles, individuals - who feels this?' },
      { activity: 'Rate the impact for each group', tip: 'Is it a big shift or a small tweak for them?' },
      { activity: 'Identify who might resist', tip: 'Who stands to lose something? Who\'s been burned before?' },
      { activity: 'Check how ready people are', tip: 'Have they been through change recently? What\'s their capacity?' },
      { activity: 'Understand what matters to them', tip: 'What do they care about? Job security? Workload? Skills?' },
    ]
  },
  plan: {
    icon: FileText,
    title: 'Plan',
    description: 'Design your approach to bring people along',
    timeframe: 'Before you start building',
    fields: [
      { key: 'comms_approach', label: 'Communications Plan' },
      { key: 'training_needs', label: 'Training & Support' },
      { key: 'stakeholder_strategy', label: 'Key Stakeholders' },
      { key: 'resistance_plan', label: 'Resistance Strategy' },
    ],
    recommended: [
      { activity: 'Plan your key messages', tip: 'What do people need to hear? Why, what, how, when?' },
      { activity: 'Identify training needs', tip: 'What new skills or knowledge do people need?' },
      { activity: 'Map your key stakeholders', tip: 'Who needs to be onside? Sponsors, managers, influencers?' },
      { activity: 'Plan for resistance', tip: 'How will you address concerns? Who can help?' },
      { activity: 'Decide how you\'ll measure success', tip: 'How will you know if people have adopted the change?' },
    ]
  },
  build: {
    icon: Megaphone,
    title: 'Build',
    description: 'Create what you need to support the change',
    timeframe: 'Before go-live',
    fields: [
      { key: 'comms_materials', label: 'Comms Created' },
      { key: 'training_materials', label: 'Training Ready' },
      { key: 'manager_prep', label: 'Managers Prepared' },
      { key: 'sponsor_ready', label: 'Sponsors Briefed' },
    ],
    recommended: [
      { activity: 'Create your communications', tip: 'Emails, presentations, FAQs - whatever people need' },
      { activity: 'Build training materials', tip: 'Guides, videos, workshops - make it easy to learn' },
      { activity: 'Brief and prepare managers', tip: 'They\'ll get the questions - make sure they have answers' },
      { activity: 'Get sponsors visible', tip: 'People need to hear from leadership why this matters' },
      { activity: 'Set up support channels', tip: 'Where do people go when they\'re stuck?' },
    ]
  },
  implement: {
    icon: Rocket,
    title: 'Implement',
    description: 'Support people through the transition',
    timeframe: 'During go-live',
    fields: [
      { key: 'go_live_support', label: 'Go-Live Support' },
      { key: 'issues_addressed', label: 'Issues Handled' },
      { key: 'adoption_tracking', label: 'Adoption Progress' },
    ],
    recommended: [
      { activity: 'Provide hands-on support at go-live', tip: 'Be visible, be available, answer questions' },
      { activity: 'Address issues quickly', tip: 'Don\'t let problems fester - tackle them fast' },
      { activity: 'Track who\'s adopting (and who isn\'t)', tip: 'Are people actually using the new way?' },
      { activity: 'Celebrate early wins', tip: 'Share success stories, recognise effort' },
      { activity: 'Keep communicating', tip: 'People need reassurance during transition' },
    ]
  },
  embed: {
    icon: Heart,
    title: 'Embed',
    description: 'Make the change stick long-term',
    timeframe: 'After go-live',
    fields: [
      { key: 'reinforcement', label: 'Reinforcement Actions' },
      { key: 'stragglers', label: 'Non-Adopters Addressed' },
      { key: 'success_measures', label: 'Success Measured' },
    ],
    recommended: [
      { activity: 'Reinforce the new behaviours', tip: 'Recognition, feedback, reminders - keep it visible' },
      { activity: 'Address people who haven\'t adopted', tip: 'Understand why, provide extra support' },
      { activity: 'Remove old ways of working', tip: 'Make it hard to go back to the old way' },
      { activity: 'Measure and share success', tip: 'Show people the change is working' },
      { activity: 'Hand over to business as usual', tip: 'Transition ownership to line managers' },
    ]
  },
};

// Map old stage names to new ones
const STAGE_MAPPING = {
  understand: 'assess',
  assess: 'assess',
  plan: 'plan',
  engage: 'build',
  deliver: 'implement',
  embed: 'embed',
};

export default function InsightsDrawer({ projectId, stage, onClose }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);

  // Map the stage to our change lifecycle
  const mappedStage = STAGE_MAPPING[stage] || 'assess';
  const config = STAGE_CONFIG[mappedStage] || STAGE_CONFIG.assess;
  const Icon = config.icon;

  useEffect(() => {
    fetchInsights();
  }, [projectId, stage]);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${projectId}/insights`);
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const extractInsights = async () => {
    try {
      setExtracting(true);
      const response = await axios.post(`${API}/projects/${projectId}/extract-insights`);
      if (response.data.insights) {
        setInsights(prev => ({
          ...prev,
          ...response.data.insights,
          timeline: response.data.timeline || {},
          updated_at: new Date().toISOString()
        }));
        toast.success('Insights updated from conversation');
      }
    } catch (error) {
      console.error('Error extracting insights:', error);
      toast.error('Failed to extract insights');
    } finally {
      setExtracting(false);
    }
  };

  // Get data from both old and new field names
  const getStageData = () => {
    if (!insights) return {};
    
    // Combine data from potentially multiple old stage mappings
    const combinedData = {};
    const oldStageNames = Object.keys(STAGE_MAPPING).filter(k => STAGE_MAPPING[k] === mappedStage);
    
    oldStageNames.forEach(oldName => {
      const oldData = insights[oldName] || {};
      Object.keys(oldData).forEach(key => {
        if (Array.isArray(oldData[key]) && oldData[key].length > 0) {
          combinedData[key] = [...(combinedData[key] || []), ...oldData[key]];
        }
      });
    });
    
    // Also check the new stage name
    const newData = insights[mappedStage] || {};
    Object.keys(newData).forEach(key => {
      if (Array.isArray(newData[key]) && newData[key].length > 0) {
        combinedData[key] = [...(combinedData[key] || []), ...newData[key]];
      }
    });
    
    return combinedData;
  };

  const stageData = getStageData();
  const hasData = Object.values(stageData).some(arr => Array.isArray(arr) && arr.length > 0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Icon className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-900">{config.title}</h2>
                <p className="text-sm text-slate-500">{config.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="close-insights-drawer"
            >
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto h-[calc(100%-140px)]">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stage timing */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      {config.timeframe}
                    </span>
                  </div>
                </div>

                {/* What you should be doing - checklist style */}
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-slate-900 mb-3">What to do at this stage</h3>
                  <ul className="space-y-3">
                    {config.recommended.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Circle className="h-5 w-5 text-slate-300 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-slate-700">{item.activity}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{item.tip}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* What you've captured from conversation */}
                {hasData && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-emerald-800 mb-3">From your conversation</h3>
                    {Object.entries(stageData).map(([key, items]) => {
                      if (!Array.isArray(items) || items.length === 0) return null;
                      
                      // Format the key nicely
                      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                      
                      return (
                        <div key={key} className="mb-3 last:mb-0">
                          <h4 className="text-xs font-medium text-emerald-700 mb-1">{label}</h4>
                          <ul className="space-y-1">
                            {items.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-emerald-700">
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Helpful tip */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">
                    <strong>Tip:</strong> Keep chatting with the coach and click "Refresh" to capture more insights from your conversation.
                  </p>
                </div>

                {/* Last updated */}
                {insights?.updated_at && (
                  <p className="text-xs text-slate-400 text-center">
                    Last updated: {new Date(insights.updated_at).toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
            <Button
              onClick={extractInsights}
              disabled={extracting}
              className="w-full bg-slate-900 hover:bg-slate-800"
              data-testid="refresh-insights-btn"
            >
              {extracting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extracting from chat...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh from Conversation
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
